import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { API } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsSearching,
  setListing,
  setNextToken,
  setPrevNextToken,
  setSearchText,
  setSelected,
} from "../store/ducks/user";
import { HeadCell } from "../models/dataTable";
import {
  CreateUserInput,
  ModelSortDirection,
  Tier,
  UpdateUserInput,
  User,
} from "../models/GQL_API";
import useApp from "./useApp";
import useAccount from "./useAccount";
import { capitalizeWords } from "../helpers/utils";
import useTimeline from "./useTimeline";
import useGroup from "./useGroup";
import {
  getUser,
  listUsers,
  usersByCardCode,
  usersByEmail,
  usersByPhoneNumber,
  userSortedByOrders,
} from "../graphql/queries";
import { createUser, deleteUser, updateUser } from "../graphql/mutations";
import {
  ListingVariables,
  TransactionListingVariables,
  UserListingVariables,
} from "../models/app";
import useTier from "./useTier";
import * as XLSX from "xlsx";
import useCardCode from "./useCardCode";

const useUser = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { session, showConfirm, showError, setSelectedUser } = useApp();

  // HOOKS
  const { accountsSelected } = useAccount("accounts", "account");
  const { groupsSelectedFilters, groupsOptions, groupsGetName } = useGroup(
    "groups",
    "group",
  );
  const { tiersUpdate, tiersGet, tiersGetName, tiersSelectedFilters } = useTier(
    "tiers",
    "tier",
  );
  const {
    cardCodesFetchByUser,
    cardCodesFetchByCode,
    cardCodesCreate,
    cardCodesTrashByUserID,
  } = useCardCode("cardCodes", "cardCode");
  const { timelinesCreate } = useTimeline("timelines", "timelines");

  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]["nextToken"],
  );
  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"],
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"],
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"],
  );
  const isSearching = useSelector(
    (state: any) => state[`${listingName}`]["isSearching"],
  );

  const account = useSelector((state: any) => state.accounts.selected);
  const guestsListing = useSelector((state: any) => state.guests.listing);

  async function fetch(props: ListingVariables) {
    let { searchText, limit } = props;

    const filter: any = { deleted: { eq: "0" } };
    filter.or = [];
    filter.and = [];

    if (searchText && searchText.length > 0) {
      searchText = searchText.trim().toLowerCase();
      filter.or = [
        { given_name: { contains: searchText } },
        { family_name: { contains: searchText } },
        { nickname: { contains: searchText } },
        { name: { contains: searchText } },
        { username: { contains: searchText } },
        { email: { contains: searchText } },
        { phone_number: { beginsWith: searchText } },
      ];
    }

    if (groupsSelectedFilters && groupsSelectedFilters.length > 0) {
      let or = [];
      for (let filter of groupsSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (tiersSelectedFilters && tiersSelectedFilters.length > 0) {
      let or = [];
      for (let filter of tiersSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (filter.and && filter.and.length === 0) {
      delete filter.and;
    }
    if (filter.or && filter.or.length === 0) {
      delete filter.or;
    }
    try {
      const response: any = await API.graphql({
        query: listUsers,
        variables: {
          limit: limit,
          filter,
          nextToken: null, //nextToken
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let nextToken = response.data.listUsers.nextToken;
      let listing = response.data.listUsers.items;

      dispatch(setListing(listing));
      dispatch(setNextToken(nextToken));

      return listing;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  //////////////////////// NEW FETCH + SEARCH + Pagination FUNCTION ////////////////////////

  function resolveQuery(searchText?: string, searchType?: string) {
    if (!searchText?.trim()) {
      return { query: listUsers, mode: "LIST" };
    }

    if (searchType === "phone" && /^\d{10,}$/.test(searchText)) {
      return { query: usersByPhoneNumber, mode: "INDEX" };
    }

    if (searchType === "email" && searchText.includes("@")) {
      return { query: usersByEmail, mode: "INDEX" };
    }

    if (searchType === "card_code" && searchText.length >= 6) {
      return { query: usersByCardCode, mode: "INDEX" };
    }

    return { query: listUsers, mode: "LIST" };
  }

  async function fetchGuests({
    searchText,
    searchType,
    tierId,
    groupId,
    limit,
    cursor,
  }: UserListingVariables) {
    const { query, mode } = resolveQuery(searchText, searchType);

    const filter: any = {
      deleted: { eq: "0" },
    };

    if (tierId) filter.tier = { eq: tierId };
    if (groupId) filter.group = { eq: groupId };

    let variables: any = {
      limit,
      nextToken: cursor ?? null,
      filter,
    };

    // INDEX queries require exact key condition
    if (mode === "INDEX" && cursor === null) {
      if (searchType === "phone") variables.phone_number = searchText;
      if (searchType === "email") variables.email = searchText;
      if (searchType === "card_code") variables.cardCode = searchText;

      const indexRes: any = await API.graphql({
        query,
        variables,
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const key = Object.keys(indexRes.data)[0];
      const data = indexRes.data[key];

      // ✅ Use index result if it returned something
      if (data.items.length > 0 || data.nextToken) {
        return {
          items: data.items,
          nextToken: data.nextToken,
        };
      }
    }

    // LIST query with partial search
    if (searchText?.trim()) {
      const s = searchText.toLowerCase();
      filter.or = [
        { name: { contains: s } },
        { email: { contains: s } },
        { phone_number: { contains: s } },
        { cardCode: { contains: s } },
        { cardCodes: { contains: s } },
      ];
    }

    variables = {
      limit,
      nextToken: cursor ?? null,
      filter,
    };

    const response: any = await API.graphql({
      query: listUsers,
      variables,
      authMode: session
        ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
        : GRAPHQL_AUTH_MODE.AWS_IAM,
    });

    const key = Object.keys(response.data)[0];

    return {
      items: response.data[key].items,
      nextToken: response.data[key].nextToken,
    };
  }

  //////////////////////// OLD FETCH FUNCTION ////////////////////////
  async function fetchWithPagination(
    params: UserListingVariables,
    currentListing?: any[],
  ) {
    try {
      const { searchText, tierId, groupId, searchType, limit } = params;

      // Use provided currentListing if available, otherwise use guestsListing from selector
      const baseListing =
        currentListing !== undefined ? currentListing : guestsListing;

      // If starting fresh (empty listing), always use null for nextToken (first request)
      // Otherwise, get current nextToken from Redux, but use null if not set
      const currentNextToken =
        baseListing.length === 0 ? null : nextToken || null;

      const filter: any = {
        accountID: { eq: account.id },
        deleted: { eq: "0" }, // Add deleted filter
      };

      filter.or = [];
      filter.and = [];

      // If there's a search text and search type, use appropriate query based on search type
      if (searchText && searchText.trim() !== "" && searchType) {
        if (searchType === "phone") {
          // For phone search, check if it's a partial match (needs contains filter)
          const isPartialPhone =
            searchText.length < 10 || !/^\d+$/.test(searchText);

          if (isPartialPhone) {
            // Partial phone search - use listUsers with contains filter
            filter.phone_number = { contains: searchText };
            // Continue to listUsers query below
          } else {
            // Exact phone match - use index query
            const fastListingSearch = await fastListing(params, baseListing);
            return fastListingSearch || [];
          }
        } else if (searchType === "email") {
          // Email search - try index query first (for exact matches)
          // For partial email searches, we'll use listUsers with contains
          const isPartialEmail =
            !searchText.includes("@") || searchText.length < 5;

          if (isPartialEmail) {
            // Partial email search - use listUsers with contains filter
            filter.email = { contains: searchText };
            // Continue to listUsers query below
          } else {
            // Exact email match - use index query
            const fastListingSearch = await fastListing(params, baseListing);
            return fastListingSearch || [];
          }
        } else if (searchType === "card_code") {
          // Card code search - always use index query if possible, otherwise use contains
          const fastListingSearch = await fastListing(params, baseListing);
          // If index query returns empty and search looks partial, try contains filter
          if (
            (!fastListingSearch || fastListingSearch.length === 0) &&
            searchText.length < 10
          ) {
            const normalizedCardCode = searchText.replace(/\s+/g, "");
            filter.cardCode = { contains: normalizedCardCode };
            // Continue to listUsers query below
          } else {
            return fastListingSearch || [];
          }
        } else if (searchType === "name") {
          // Name search - only search by name fields
          const searchLower = searchText.toLowerCase().trim();
          filter.or = [
            { name: { contains: searchLower } },
            { given_name: { contains: searchLower } },
            { family_name: { contains: searchLower } },
          ];
        }
      }

      if (tierId) filter.tier = { eq: tierId };
      if (groupId) filter.group = { eq: groupId };

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      const usersList: any = await API.graphql({
        query: listUsers,
        variables: {
          filter,
          limit: limit || 1000, // Use limit from params, default to 1000 if not provided
          nextToken: currentNextToken, // Use current token, null on first request
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      const newNextToken = usersList.data.listUsers.nextToken;
      const responseListing = usersList.data.listUsers.items;

      // Remove duplicates by id
      const uniqueResponseListing = responseListing.filter(
        (item: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.id === item.id),
      );

      // baseListing was already defined above
      let listing = [...baseListing, ...uniqueResponseListing];

      // Remove duplicates from merged listing
      const finalListing = listing.filter(
        (item: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.id === item.id),
      );

      dispatch(setPrevNextToken(currentNextToken));
      dispatch(setNextToken(newNextToken));
      return finalListing;
    } catch (err: Error | any) {
      console.log(err);
      // showError(err.message || err);
      return [];
    }
  }

  async function fastListing(
    params: UserListingVariables,
    currentListing?: any[],
  ) {
    try {
      const { searchText, tierId, groupId, searchType, limit } = params;

      console.log({ searchType });
      console.log({ searchText });

      // Use provided currentListing if available, otherwise use guestsListing from selector
      const baseListing =
        currentListing !== undefined ? currentListing : guestsListing;

      // If starting fresh (empty listing), always use null for nextToken (first request)
      // Otherwise, get current nextToken from Redux, but use null if not set
      const fastListingNextToken =
        baseListing.length === 0 ? null : nextToken || null;

      let query: any = listUsers;
      const filter: any = {
        accountID: { eq: account.id },
        deleted: { eq: "0" },
      };
      let variables: any = {
        deleted: "0",
        limit: limit || 1000, // Use limit from params, default to 1000 if not provided
        nextToken: fastListingNextToken, // Use current token, null on first request
        filter,
      };

      if (searchText && searchText.trim() !== "") {
        if (searchType === "phone") {
          // For phone search, try index query first (for exact matches)
          // If it's likely a partial match (short string or contains non-digits), use listUsers with contains
          // Index queries require exact match, so partial searches need listUsers
          const isLikelyPartial =
            searchText.length < 10 || !/^\d+$/.test(searchText);

          if (isLikelyPartial) {
            // Use listUsers with contains filter for partial phone searches
            filter.phone_number = { contains: searchText };
            // Don't change query - use listUsers
          } else {
            // Try exact match with index query
            query = usersByPhoneNumber;
            variables = {
              phone_number: searchText,
              deleted: "0",
              nextToken: fastListingNextToken,
              filter,
            };
          }
        } else if (searchType === "email") {
          query = usersByEmail;
          variables = {
            email: searchText,
            deleted: "0",
            // No limit - fetch all results
            nextToken: fastListingNextToken,
            filter,
          };
        } else if (searchType === "name") {
          // Optimized name search - use case-insensitive search with fewer conditions
          // DynamoDB/AppSync handles case-insensitive contains efficiently
          const searchLower = searchText.toLowerCase().trim();
          filter.or = [
            { name: { contains: searchLower } },
            { given_name: { contains: searchLower } },
            { family_name: { contains: searchLower } },
          ];
        } else if (searchType === "card_code") {
          // Normalize card code by removing spaces for search
          const normalizedCardCode = searchText.replace(/\s+/g, "");
          // Use index query for card code search when possible
          // For exact matches, we can use usersByCardCode, but for partial we use contains
          // Since usersByCardCode requires exact match, we'll use contains for flexibility
          // but normalize to handle spaces
          filter.cardCode = { contains: normalizedCardCode };
        }
      }

      // Add group filter if groupId exists
      if (groupId) {
        filter.group = { eq: groupId };
      }

      // Add tier filter if tierId exists
      if (tierId) {
        filter.tier = { eq: tierId };
      }

      // Clean up empty filter arrays
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }
      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }

      // Update variables with the filter
      variables.filter = filter;

      // if ((!searchText || searchText.trim() === "") && groupId) {
      //   query = ByGroup;
      //   variables = {
      //     group: groupId,
      //     deleted: "0",
      //     limit: 100000,
      //     nextToken: nextToken,
      //     filter,
      //   };
      // }

      // Execute the query
      const response: any = await API.graphql({
        query,
        variables,
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      // Extract response data dynamically based on query
      const responseKey = Object.keys(response.data)[0]; // Assumes query response key is dynamic
      const newNextToken = response.data[responseKey].nextToken;
      console.log({ newNextToken });
      console.log({ responseKey });
      const responseListing = response.data[responseKey].items;
      console.log({ responseListing });
      // Remove duplicates by id to prevent duplicate users in search results
      const uniqueListing = responseListing.filter(
        (item: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.id === item.id),
      );

      // Merge with baseListing (currentListing if provided, otherwise guestsListing) and remove duplicates
      const mergedListing = [...baseListing, ...uniqueListing];
      const finalListing = mergedListing.filter(
        (item: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.id === item.id),
      );

      dispatch(setNextToken(newNextToken));
      dispatch(setPrevNextToken(fastListingNextToken));
      return finalListing;
    } catch (err: Error | any) {
      console.log(err);
      return [];
    }
  }

  async function fetchAll(params: UserListingVariables) {
    try {
      const { searchText, tierId, groupId, limit } = params;
      const filter: any = { accountID: { eq: account.id } };

      filter.or = [];
      filter.and = [];
      const capitalizedSearchText = capitalizeWords(searchText);

      if (searchText && searchText.length > 0) {
        filter.or.push({ name: { contains: searchText.toLowerCase() } });
        filter.or.push({ name: { contains: searchText } });
        filter.or.push({ name: { contains: capitalizedSearchText } });

        filter.or.push({ email: { contains: searchText.toLowerCase() } });
        filter.or.push({ email: { contains: searchText } });
        filter.or.push({ email: { contains: capitalizedSearchText } });

        filter.or.push({ phone_number: { contains: searchText } });

        filter.or.push({ cardCode: { contains: searchText } });
      }

      if (tierId) filter.tier = { eq: tierId };
      if (groupId) filter.group = { eq: groupId };

      if (filter.and.length === 0) delete filter.and;
      if (filter.or.length === 0) delete filter.or;

      let listing: any[] = [];

      // Try fast listing first
      const fastListingSearch = await fastListingAll(params);
      if (fastListingSearch && fastListingSearch.length > 0) {
        return fastListingSearch;
      }

      console.log("fast listing failed");

      let nextToken: string | null = null;

      do {
        const usersList: any = await API.graphql({
          query: listUsers,
          variables: {
            filter,
            limit: limit, // Increased limit for large datasets
            nextToken,
            // deleted: "0",
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        nextToken = usersList.data.listUsers.nextToken;
        const responseListing = usersList.data.listUsers.items;

        // Remove duplicates by id
        const uniqueResponseListing = responseListing.filter(
          (item: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.id === item.id),
        );

        // Merge and remove duplicates from merged list
        listing = [...listing, ...uniqueResponseListing];
        listing = listing.filter(
          (item: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.id === item.id),
        );
      } while (nextToken);

      return listing;
    } catch (err: Error | any) {
      console.log(err);
      return [];
    }
  }

  async function fastListingAll(params: UserListingVariables) {
    try {
      const { searchText, groupId, tierId, searchType } = params;

      let query: any = listUsers;
      const filter: any = { deleted: { eq: "0" } };
      let variables: any = {
        deleted: "0",
        limit: 10000, // Increased limit for large datasets
        nextToken: null,
        filter,
      };

      console.log({ paramsparams: params });

      if (searchText && searchText.trim() !== "") {
        if (searchType === "phone") {
          console.log("in here with phone");

          query = usersByPhoneNumber;
          variables = {
            phone_number: searchText,
            limit: 10000, // Increased limit
            ...variables,
          };
        } else if (searchType === "email") {
          query = usersByEmail;
          variables = {
            email: searchText,
            limit: 10000, // Increased limit
            ...variables,
          };
        } else if (searchType === "card_code") {
          // Normalize card code for search
          const normalizedCardCode = searchText.replace(/\s+/g, "");
          // Try to use usersByCardCode index for exact match
          query = usersByCardCode;
          variables = {
            cardCode: normalizedCardCode,
            limit: 10000, // Increased limit
            ...variables,
          };
        }
      }

      if (searchText && searchText.trim() !== "" && groupId) {
        variables.filter.group = { eq: groupId };
      }

      let listing: any[] = [];
      let nextToken: string | null = null;

      do {
        variables.nextToken = nextToken;
        const response: any = await API.graphql({
          query,
          variables,
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        const responseKey = Object.keys(response.data)[0];
        nextToken = response.data[responseKey].nextToken;
        const responseListing = response.data[responseKey].items;

        // Remove duplicates by id
        const uniqueResponseListing = responseListing.filter(
          (item: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.id === item.id),
        );

        // Merge and remove duplicates from merged list
        listing = [...listing, ...uniqueResponseListing];
        listing = listing.filter(
          (item: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.id === item.id),
        );
      } while (nextToken);

      return listing;
    } catch (err: Error | any) {
      console.log(err);
      return [];
    }
  }

  // FETCH BY INDEX
  async function fetchOrdered(props: ListingVariables) {
    let { searchText, limit } = props;

    const filter: any = {};
    filter.or = [];
    filter.and = [];

    if (searchText && searchText.length > 0) {
      searchText = searchText.trim().toLowerCase();
      filter.or = [
        { given_name: { contains: searchText } },
        { family_name: { contains: searchText } },
        { nickname: { contains: searchText } },
        { name: { contains: searchText } },
        { username: { contains: searchText } },
        { email: { contains: searchText } },
        { phone_number: { beginsWith: searchText } },
      ];
    }

    if (groupsSelectedFilters && groupsSelectedFilters.length > 0) {
      let or = [];
      for (let filter of groupsSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (tiersSelectedFilters && tiersSelectedFilters.length > 0) {
      let or = [];
      for (let filter of tiersSelectedFilters) {
        or.push({ tier: { eq: filter.id } });
      }
      if (or.length > 0) filter.and.push({ or: or });
    }

    if (filter.and && filter.and.length === 0) {
      delete filter.and;
    }
    if (filter.or && filter.or.length === 0) {
      delete filter.or;
    }

    try {
      const response: any = await API.graphql({
        query: userSortedByOrders,
        variables: {
          deleted: "0",
          filter,
          limit: limit,
          nextToken: null,
          sortDirection: ModelSortDirection.DESC,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let nextToken = response.data.userSortedByOrders.nextToken;
      let listing = response.data.userSortedByOrders.items;

      dispatch(setListing(listing));
      return listing;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  async function fetchByEmail(email: string) {
    try {
      const response: any = await API.graphql({
        query: usersByEmail,
        variables: {
          email: email,
          filter: { deleted: { eq: "0" } },
          limit: 1,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const listing = response.data.usersByEmail.items;
      return listing.length > 0 ? listing[0] : null;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  async function fetchByPhoneNumber(phone_number: string) {
    try {
      const response: any = await API.graphql({
        query: usersByPhoneNumber,
        variables: {
          phone_number: phone_number,
          filter: { deleted: { eq: "0" } },
          limit: 1,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const listing = response.data.usersByPhoneNumber.items;
      return listing.length > 0 ? listing[0] : null;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  async function fetchByCode(cardCode: string, accountID: string) {
    try {
      // Normalize card code by removing spaces
      const normalizedCardCode = cardCode.replace(/\s+/g, "");

      // Fetch users by accountID and check if any have matching normalized card code
      const response: any = await API.graphql({
        query: usersByCardCode,
        variables: {
          cardCode: normalizedCardCode,
          filter: {
            accountID: { eq: accountID },
            deleted: { eq: "0" },
          },
          limit: 1000,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let users = response.data.usersByCardCode.items;

      // Check if any user has a card code that matches when normalized (spaces removed)
      // const matchingUser = users.find((user: any) => {
      //   if (!user.cardCode) return false;
      //   const normalizedExistingCode = user.cardCode.replace(/\s+/g, "");
      //   return normalizedExistingCode === normalizedCardCode;
      // });

      // if (matchingUser) {
      //   return matchingUser;
      // }

      return users.length > 0 ? users[0] : null;
    } catch (err) {
      showError(err);
      return null;
    }
  }

  /**
   * Get Resource Name
   *
   * @param id id: string
   *
   * @returns string
   */
  const getName = (id: string) => {
    if (listing.length > 0) {
      const model = listing.find((model: User) => model.id === id);
      return model ? model.name : "";
    } else {
      return "";
    }
  };

  async function get(resourceId: string) {
    try {
      const user: any = await API.graphql({
        query: getUser,
        variables: { id: resourceId },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      return user.data.getUser;
    } catch (err) {
      showError(err);
      return null;
    }
  }

  /**
   * Construct name from given_name, middle_name, and family_name
   *
   * @param given_name given_name: string
   * @param middle_name middle_name: string
   * @param family_name family_name: string
   *
   * @returns string
   */
  const constructName = (
    given_name: string,
    middle_name: string,
    family_name: string,
  ) => {
    if (
      given_name.length > 0 &&
      middle_name.length > 0 &&
      family_name.length > 0
    ) {
      return (
        given_name.trim() + " " + middle_name.trim() + " " + family_name.trim()
      );
    }

    if (given_name.length > 0 && family_name.length > 0) {
      return given_name.trim() + " " + family_name.trim();
    }

    if (given_name.length > 0 && middle_name.length > 0) {
      return given_name.trim() + " " + middle_name.trim();
    }

    return given_name;
  };

  async function create(data: any, updateTierFlag = true) {
    try {
      if (!data.phone_number && !data.email && !data.cardCode) {
        throw new Error("Please provide Phone, Email or Card Code.");
      }

      if (data.phone_number) {
        let userExists = await fetchByPhoneNumber(data.phone_number);
        if (userExists) throw new Error("Phone Number already exists");
      }

      if (data.email) {
        let userExists = await fetchByEmail(data.email);
        if (userExists) throw new Error("Email already exists");
      }

      const rawCardCode = data.cardCode?.trim();
      if (rawCardCode) {
        const normalizedCardCode = rawCardCode.replace(/\s+/g, "");
        const errorMessage = `Card code already exists ${rawCardCode}. Card code must be unique.`;

        const [userExists, membershipCardExists] = await Promise.all([
          fetchByCode(normalizedCardCode, accountsSelected.id),
          cardCodesFetchByCode(normalizedCardCode),
        ]);

        if (userExists || membershipCardExists) {
          throw new Error(errorMessage);
        }
      }

      if (data.birthdate) {
        if (
          data.birthdate <= "1940-01-01" ||
          data.birthdate >= `${new Date().getFullYear() - 10}-01-01`
        ) {
          throw new Error("Invalid Date of Birth");
        }
      }

      const tier: Tier = await tiersGet(data.tier ?? "");
      const createInput: CreateUserInput = {
        accountID: accountsSelected.id,
        phone_number: data.phone_number ? data.phone_number : "N/A",
        email: data.email ? data.email : "N/A",
        cardCodes: data.cardCode ? [data.cardCode] : [],
        cardCode: data.cardCode ? data.cardCode.replace(/\s+/g, "") : "", // Remove spaces when saving

        given_name: data.given_name ? data.given_name.toLowerCase() : "",
        middle_name: data.middle_name ? data.middle_name.toLowerCase() : "",
        family_name: data.family_name ? data.family_name.toLowerCase() : "",
        nickname: data.nickname ? data.nickname.toLowerCase() : "",
        name: data.name
          ? data.name.toLowerCase()
          : constructName(
              data.given_name ? data.given_name.toLowerCase() : "",
              data.middle_name ? data.middle_name.toLowerCase() : "",
              data.family_name ? data.family_name.toLowerCase() : "",
            ),
        address: data.address ? data.address : "",
        birthdate: data.birthdate ? data.birthdate : "",
        picture: data.picture ? data.picture : "",
        facebook: data.facebook ? data.facebook : "",
        instagram: data.instagram ? data.instagram : "",
        username: data.username ? data.username : "",
        group: data.group
          ? data.group
          : groupsOptions.length > 0
            ? groupsOptions.find(
                (item: any) =>
                  item.label === "normal" || item.label === "Normal",
              ).value
            : "",

        stats: [],
        total_points: data.total_points ? Number(data.total_points) : 0,
        points_history: data.points_history ? data.points_history : [],
        tier: data.tier ? data.tier : "",
        tier_name: tier ? tier.name : "",
        total_spending: 0,
        orders_count: 0,
        deleted: "0",
        createdAt: new Date().toISOString(),
        createdByID: session.sub,
        createdByName: session.name,
      };

      let createdUser: any = await API.graphql<User>({
        query: createUser,
        variables: { input: createInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      createdUser = createdUser.data.createUser;

      // Create membership card code entry
      if (rawCardCode) {
        const membershipCardInput: any = {
          cardID: data.memberID ? data.memberID : rawCardCode,
          code: rawCardCode,
          tier: createdUser.tier_name.toLowerCase(),
          points_balance: createdUser.points_balance,
          startDate: data.startDate
            ? data.startDate
            : new Date().toISOString().split("T")[0], // example: 2026-01-22
          endDate: data.endDate
            ? data.endDate
            : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                .toISOString()
                .split("T")[0],
          status: "active",
        };

        await cardCodesCreate(membershipCardInput, createdUser.id);
      }

      // Update Tier Stats
      if (tier && updateTierFlag)
        await tiersUpdate(tier.id, {
          customerCount: tier.customerCount + 1,
        });

      dispatch(setListing([createdUser, ...listing]));
      showConfirm(`New ${singleName} has been created successfully`);
      return createdUser;
    } catch (err: Error | any) {
      showError(err.message || err);
      throw err;
    }
  }

  async function update(resourceId: string, data: any) {
    const flagsName: string[] = [];
    const interestsName: string[] = [];

    try {
      const original = await get(resourceId);
      if (!original) {
        showError("Can not find this user.");
        return;
      }
      let name = original!.name;

      if (data.given_name && data.given_name && data.family_name) {
        name = constructName(
          data.given_name.toLowerCase(),
          "",
          data.family_name.toLowerCase(),
        );
      }

      data = {
        ...data,
        tier_name: data.tier ? tiersGetName(data.tier) : original!.tier_name,
      };
      const updateInput: UpdateUserInput = {
        id: original.id,
        conceptID: data.conceptID ? data.conceptID : original!.conceptID,
        phone_number: data.phone_number
          ? data.phone_number
          : original!.phone_number,
        cardCodes: data.cardCodes ? data.cardCodes : original!.cardCodes,
        cardCode:
          data.cardCode !== undefined ? data.cardCode : original!.cardCode,
        cardCodeID:
          data.cardCodeID !== undefined
            ? data.cardCodeID
            : original!.cardCodeID,

        given_name: data.given_name
          ? data.given_name.toLowerCase()
          : original!.given_name,
        middle_name: data.middle_name
          ? data.middle_name.toLowerCase()
          : original!.middle_name,
        nickname: data.nickname
          ? data.nickname.toLowerCase()
          : original!.nickname,
        family_name: data.family_name
          ? data.family_name.toLowerCase()
          : original!.family_name,
        name: name.toLowerCase(),
        address: data.address ? data.address : original!.address,
        birthdate: data.birthdate ? data.birthdate : original!.birthdate,
        picture: data.picture ? data.picture : original!.picture,
        facebook: data.facebook ? data.facebook : original!.facebook,
        instagram: data.instagram ? data.instagram : original!.instagram,
        username: data.username ? data.username : original!.username,
        group: data.group ? data.group : original!.group,
        email: data.email ? data.email : original!.email,
        stats: data.stats ? data.stats : original!.stats,
        total_points: data.total_points
          ? data.total_points
          : original!.total_points,
        points_history: data.points_history
          ? data.points_history
          : original!.points_history,
        tier: data.tier ? data.tier : original!.tier,
        tier_name: data.tier_name ? data.tier_name : original!.tier_name,
        points_balance: data.points_balance
          ? data.points_balance
          : original!.points_balance,
        total_spending: data.total_spending
          ? data.total_spending
          : original!.total_spending,
        orders_count: data.orders_count
          ? data.orders_count
          : original!.orders_count,

        createdAt: data.createdAt ? data.createdAt : original!.createdAt,
        _version: original!._version,
      };

      let updatedUser: any = await API.graphql<User>({
        query: updateUser,
        variables: { input: updateInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      updatedUser = updatedUser.data.updateUser;

      // Update Tier Stats if tier changed
      if (data.tier && original!.tier !== data.tier) {
        const oldTier: Tier = await tiersGet(original!.tier ?? "");
        const newTier: Tier = await tiersGet(data.tier ?? "");

        if (oldTier.id) {
          await tiersUpdate(oldTier.id, {
            customerCount: oldTier.customerCount - 1,
          });
        }
        if (newTier.id) {
          await tiersUpdate(newTier.id, {
            customerCount: newTier.customerCount + 1,
          });
        }
      }

      await timelinesCreate(
        { ...data, customerId: resourceId, name, flagsName, interestsName },
        original!,
      );

      setSelectedUser(updatedUser);

      showConfirm(`${singleName} has been updated successfully`);
      return updatedUser;
    } catch (err: Error | any) {
      showError(err.message || err);
      return null;
    }
  }

  /**
   * Fix user creation date
   * Fix total points according to total spending
   * Return to normal tier
   *
   * @returns void
   */
  async function fixUserData() {
    try {
      let params: UserListingVariables = {
        searchText: "",
        startIndex: 0,
        limit: 10000,
      };

      const users: User[] = await fetchAll(params);

      if (users) {
        for (let user of users) {
          let points_balance =
            user && user.total_spending ? user.total_spending / 10 : 0;
          const updateUser = {
            total_points: user.total_spending
              ? Math.floor(user.total_spending)
              : 0, // (Rate = 1)
            points_balance: Number(points_balance.toFixed(2)), // (Rate = 10)
            tier: "8a69da49-63d1-48ed-b142-c44266eca42e",
            tier_name: "normal tier",
            createdAt: new Date(user.createdAt).toISOString(),
          };

          const updated = await update(user.id, updateUser);
          // console.log({ updated });
        }
      }
      showConfirm(`Users has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function updateEmailOnline(resourceId: string, data: any) {
    try {
      if (data.email) {
        const updated = await API.graphql({
          query: updateUser,
          variables: {
            input: {
              id: resourceId,
              email: data.email,
            },
          },
          authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        setSelectedUser(updated);
      }
    } catch (err: Error | any) {
      showError(err.message || err);
    }
  }

  async function trash(resourceId: string) {
    try {
      const original = await get(resourceId);

      await API.graphql({
        query: updateUser,
        variables: {
          input: {
            id: original.id,
            deleted: "1",
            _version: original!._version,
          },
        },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      // Trash all user cards
      await cardCodesTrashByUserID(resourceId);

      // Update the listing to remove the deleted item
      dispatch(
        setListing(
          listing.filter((resource: any) => resource.id !== resourceId),
        ),
      );

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(resourceIds: any) {
    for (let resourceId of resourceIds) {
      try {
        await trash(resourceId);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    dispatch(
      setListing(
        listing.filter((resource: any) => !resourceIds.has(resource.id)),
      ),
    );

    showConfirm(
      `${resourceIds.size} ${listingName} items has been moved to trash`,
    );
  }

  async function remove(resourceId: any) {
    try {
      const removedUser = await API.graphql({
        query: deleteUser,
        variables: {
          input: {
            id: resourceId,
          },
        },
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      console.log({
        removedUser,
      });

      dispatch(
        setListing(
          listing.filter((resource: any) => resource.id !== resourceId),
        ),
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err);
    }
  }

  async function exportAllV1(params: TransactionListingVariables) {
    try {
      let exportedData = [];

      const data = await fetch(params);

      for (let user of data!) {
        let row: any = { ...user };
        if (user.group) {
          row.groupName = groupsGetName(user.group);
        }
        exportedData.push(row);
      }

      return exportedData;
    } catch (err) {
      showError(err);
    }
  }

  async function exportAll(params: UserListingVariables) {
    try {
      const data = await fetchAll(params);
      if (!data || data.length === 0) {
        showError("No data available for export.");
        return;
      }

      // Define column headers using headCells
      const headers = headCells
        .filter((cell) => cell.id !== "actions") // Exclude "actions" column
        .map((cell) => cell.label || ""); // Ensure label is a string

      // Transform data based on headCells structure
      let exportedData = data.map((user) => {
        let row: Record<string, any> = {}; // Use Record<string, any> to avoid index type error

        headCells.forEach((cell) => {
          if (cell.id !== "actions" && cell.label) {
            // Exclude actions column and check label exists
            row[cell.label] = user[cell.id as keyof typeof user] || ""; // Use keyof typeof user to avoid type error
          }
        });

        // Add group name if group exists
        if (user.group) {
          row["Group"] = groupsGetName({
            id: user.group,
            listing: params.groupsListing ? params.groupsListing : [],
          });
        }

        if (user.cardCodes) {
          row["Card Codes"] =
            user.cardCodes && user.cardCodes.length > 0
              ? user.cardCodes.join(", ")
              : user.cardCode || "";
        }

        return row;
      });

      // Create worksheet with headers
      const worksheet = XLSX.utils.json_to_sheet(exportedData, {
        header: headers,
      });

      // Create a new workbook and append the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users Data");

      // Generate Excel file as a Blob
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const dataBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create a download link
      const url = URL.createObjectURL(dataBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "users_data.xlsx";
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(err);
    }
  }

  /*
   * Get invoices listed in CSV format
   * Convert them to JSON
   * Save this JSON in local file
   * Loop over this list to submit invoice/credit note
   *
   * */
  async function importDataExcelFile(file: File): Promise<void> {
    try {
      const newUsers: any[] = [];
      let newUsersCount = 0;
      let invalidUsersCount = 0;
      let existingUsersCount = 0;

      const reader = new FileReader();

      reader.readAsBinaryString(file);
      reader.onload = async (event) => {
        const data = event.target?.result;
        if (!data) {
          showError("File data could not be read.");
          return;
        }

        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet);

        for (const row of rows) {
          const name = (row["name"] || "").replace("'", " ").trim();
          const email = (row["email"] || "").replace("'", " ").trim();
          let phoneNumber = (
            row["phoneNumber"] ? String(row["phoneNumber"]) : ""
          )
            .replace("'", " ")
            .trim();

          const memberID = (row["memberID"] ? String(row["memberID"]) : "")
            .replace("'", " ")
            .trim();
          const cardCode = (row["cardCode"] ? String(row["cardCode"]) : "")
            .replace("'", " ")
            .trim();
          const address = row["address"] || "";
          const points = Number(row["points"]) || 0;

          if (!cardCode) {
            invalidUsersCount++;
            continue;
          }

          if (phoneNumber && !phoneNumber.startsWith("0")) {
            phoneNumber = "0" + phoneNumber;
          }

          // const existingUser = await fetchByCode(cardCode);
          // if (existingUser) {
          //   existingUsersCount++;
          //   continue;
          // }

          newUsers.push({
            given_name: name,
            email,
            phone_number: phoneNumber,
            cardCode,
            memberID,
            total_points: points,
          });

          newUsersCount++;
        }

        await Promise.all(newUsers.map((user) => create(user)));
        showConfirm(`Users imported successfully: ${newUsersCount}. 
  Already existed: ${existingUsersCount}. 
  Invalid users: ${invalidUsersCount}.`);
      };
    } catch (err: unknown) {
      const error = err as Error;
      console.error(error);
      showError(error.message || "An unexpected error occurred.");
    }
  }

  async function fixUserName() {
    // list all users
    const users = await fetchAll({
      searchText: "",
      startIndex: 0,
      limit: 10000,
    });
    for (let user of users) {
      const updateInput: UpdateUserInput = {
        id: user.id,
        name: user.name.toLowerCase(),
        _version: user._version,
      };
      await update(user.id, updateInput);
    }
  }

  async function createMembershipIfNotExists() {
    try {
      // list all users
      const users = await fetchAll({
        searchText: "",
        startIndex: 0,
        limit: 10000,
      });
      for (let user of users) {
        // if (!user.name.toLowerCase().includes("test")) continue;
        // if (user.tier !== "755cd3d3-9330-437d-bfbd-06f139cd0b60") continue; // GOLD TIER
        // if (user.tier !== "ff308750-af51-4571-8fa1-53306032bf9a") continue; // SILVER TIER

        let userCardCodes = await cardCodesFetchByUser(user.id);

        if (
          user.cardCode &&
          (user.cardCodes.length == 0 || userCardCodes.length === 0)
        ) {
          if (user.cardCodes.length > 0 && userCardCodes.length === 0) {
            // Update customer to add card code in card codes array
            const updateInput: UpdateUserInput = {
              id: user.id,
              cardCodes: [user.cardCode],
              _version: user._version,
            };
            await update(user.id, updateInput);
          }

          // Create membership card code entry
          await cardCodesCreate(
            {
              cardID: user.cardCode,
              code: user.cardCode,
              tier: user.tier_name.toLowerCase(),
              points_balance: user.points_balance,
              startDate: new Date().toISOString().split("T")[0], // example: 2026-01-22
              endDate: new Date(
                new Date().setFullYear(new Date().getFullYear() + 5),
              )
                .toISOString()
                .split("T")[0],
              status: "active",
            },
            user.id,
          );
        }
      }
    } catch (err) {
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "phone_number",
      numeric: false,
      disablePadding: false,
      label: "Mobile",
    },
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: "Email",
    },
    {
      id: "cardCode",
      numeric: false,
      disablePadding: false,
      label: "Card Codes",
    },
    {
      id: "tier_name",
      numeric: false,
      disablePadding: false,
      label: "Tier",
    },
    {
      id: "total_points",
      numeric: false,
      disablePadding: false,
      label: "Points",
    },
    {
      id: "total_spending",
      numeric: false,
      disablePadding: false,
      label: "Total Purchase",
    },
    {
      id: "points_balance",
      numeric: false,
      disablePadding: false,
      label: "Available Balance",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = [
    "name",
    "phone_number",
    "email",
    "cardCode",
    "tier_name",
    "total_points",
    "total_spending",
    "points_balance",
  ];

  const options: any[] = [];

  const api: any = {};

  // api[`${listingName}Model`] = User as any;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}IsSearching`] = isSearching;

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;

  api[`${listingName}Get`] = get;

  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchAll`] = fetchAll;
  api[`${listingName}FetchSearch`] = fetchGuests;
  api[`${listingName}FetchWithPagination`] = fetchWithPagination;

  api[`${listingName}FetchByCode`] = fetchByCode;
  api[`${listingName}FetchByPhoneNumber`] = fetchByPhoneNumber;
  api[`${listingName}FetchByEmail`] = fetchByEmail;
  api[`${listingName}FetchOrdered`] = fetchOrdered;

  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}AddConceptID`] = fixUserData;
  api[`${listingName}UpdateEmailOnline`] = updateEmailOnline;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}ImportData`] = importDataExcelFile;
  api[`${listingName}CreateMembershipIfNotExists`] =
    createMembershipIfNotExists;
  api[`${listingName}FixUserName`] = fixUserName;

  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}ChangeIsSearching`] = (payload: boolean) =>
    dispatch(setIsSearching(payload));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));

  return api;
};

export default useUser;
